import { useCallback, useMemo } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { useQueryClient } from '@tanstack/react-query';
import { loginRequest, logoutRequest } from '@lib/auth/authConfig';
import { authKeys } from '@lib/auth/auth.utils';
import { AppRouterContext } from '@shared-types/router.types';

/**
 * Hook that provides MSAL authentication state for TanStack Router context.
 * Integrates MSAL with TanStack Router's auth pattern.
 *
 * Note: This hook does NOT use router hooks (useNavigate/useRouter) since it's called
 * before RouterProvider exists. Navigation is handled by MSAL's built-in redirects.
 *
 * @returns Auth object matching AppRouterContext['auth'] interface
 */
export function useMsalRouterAuth(): AppRouterContext['auth'] {
  const {instance, accounts, inProgress} = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const queryClient = useQueryClient();

  const login = useCallback(async (): Promise<void> => {
    try {
      const loginResponse = await instance.loginPopup({
        ...loginRequest,
      });

      if (loginResponse.account) {
        instance.setActiveAccount(loginResponse.account);
        await queryClient.invalidateQueries({queryKey: authKeys.all});
      }
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  }, [instance, queryClient]);

  const logout = useCallback(async (): Promise<void> => {
    try {
      const account = instance.getActiveAccount();
      queryClient.removeQueries({queryKey: authKeys.all});

      await instance.logoutRedirect({
        ...logoutRequest,
        account,
      });
    } catch (error) {
      console.error('Logout failed:', error);
      throw error;
    }
  }, [instance, queryClient]);

  return useMemo(
    () => ({
      isAuthenticated,
      user: accounts[0] || null,
      isLoading: inProgress !== 'none',
      login,
      logout,
      msalInstance: instance,
    }),
    [isAuthenticated, accounts, inProgress, login, logout, instance]
  );
}
